import { Platform, TouchableOpacity, View } from 'react-native';
import { FONT_WEIGHT } from 'constants/design-system';

import colors from 'config/colors';
import Addons from 'modules/ChefScene/components/Addons';
import { RouteNames } from 'navigation/linkingConfig';
import { useAppSelector } from 'store/hooks';
import { NotificationsSelectors } from 'store/Notifications';
import {
  ItemPreparationStatuses,
  OrderInternalStatuses,
} from 'store/Orders/enums';
import { SettingSelectors } from 'store/Settings';
import { UserSelectors } from 'store/User';
import { AnalyticsEvents, useLogEventCallback } from 'util/analytics';
import { useCurrencyString } from 'util/helpers/currency';
import { useItemDisplayName } from 'util/hooks/useItemDisplayName';
import { Text } from 'components';
import { BaseText } from 'components/BaseText';

import { StatusButtons } from './StatusButtons';
import type { Order, OrderItem } from 'store/Orders/types';

type ItemProps = {
  order: Order;
  item: OrderItem;
  index: number;
  shouldDifferentiateLayout?: boolean;
  orderInternalStatus: OrderInternalStatuses;
  shouldDisplayActionButton?: boolean;
  onStatusChanged?: (props: {
    orderId: string;
    itemId: string;
    mode: 'single' | 'group';
    startDate: Date;
    endDate: Date;
    status: ItemPreparationStatuses;
    screen: string | undefined;
  }) => void;
  shouldPizzaAddonVerticalOnly?: boolean;
  onPress?: () => void;
  onPressedAction?: () => void;
  actionButtonArrangement?: 'vertical' | 'horizontal';
};

export const Item = (props: ItemProps) => {
  const {
    order,
    item,
    index,
    shouldDifferentiateLayout,
    orderInternalStatus,
    onStatusChanged,
    onPressedAction,
    onPress,
    shouldPizzaAddonVerticalOnly,
    shouldDisplayActionButton = true,
    actionButtonArrangement,
  } = props;

  const logEvent = useLogEventCallback();
  const getCurrencyString = useCurrencyString();

  const { shouldDisplayItemPricing } = useAppSelector(
    SettingSelectors.selectItemListInterfaceSettings,
  );

  const interfaceSettingsFeatureFlag = useAppSelector(
    UserSelectors.selectInterfaceSettings,
  );

  const hasUnacceptedNotifications = useAppSelector((state) =>
    NotificationsSelectors.selectOrderItemHasUnattended(state, item.uuid),
  );

  // const navigationProps = useLinkProps({
  //   to: {
  //     screen: RouteNames.Home.Main.ItemDetails,
  //     params: {
  //       uuid: item.uuid,
  //     },
  //   },
  // });

  const backgroundColor = hasUnacceptedNotifications
    ? colors.ui.notifications.unread
    : shouldDifferentiateLayout
      ? index % 2
        ? colors.alternateWhite
        : colors.white
      : colors.white;

  const { itemDisplayName } = useItemDisplayName({
    itemDisplayName: item?.display_names,
  });

  return (
    <TouchableOpacity
      onPress={(event) => {
        onPress?.();
        logEvent(
          AnalyticsEvents.itemCellPressed({
            screen: RouteNames.Home.Main.Drawer.Tabs.Overview._,
          }),
        );
      }}
      activeOpacity={0.75}
      style={{
        padding: 8,
        backgroundColor,
      }}
    >
      <View style={{ flexDirection: 'row', alignItems: 'flex-start' }}>
        <View style={{ flex: 1 }}>
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'flex-start',
            }}
          >
            <BaseText
              fontWeight={FONT_WEIGHT.theme.medium}
              fontSize={16}
              color={'#222'}
            >
              {`${Number.isInteger(item.quantity) ? item.quantity : 1} -`}{' '}
            </BaseText>
            <BaseText
              fontWeight={FONT_WEIGHT.theme.medium}
              fontSize={16}
              style={[
                { flexShrink: 1 },
                Platform.OS === 'web' && ({ wordBreak: 'break-word' } as any),
              ]}
              color={'#222'}
            >
              {itemDisplayName ?? item?.name}
              <BaseText fontWeight={FONT_WEIGHT.theme.medium} color="#333">
                {shouldDisplayItemPricing &&
                  interfaceSettingsFeatureFlag.item_list.item_pricing &&
                  item.price != null &&
                  ` - ${getCurrencyString(item.price)}`}
              </BaseText>
            </BaseText>
          </View>
          {item.addons && (
            <Addons
              addonsDisplayNames={item.addons_display_names}
              shouldPizzaAddonVerticalOnly={shouldPizzaAddonVerticalOnly}
              addons={item.addons}
              addonsType={item.addons_type}
            />
          )}
        </View>
        {shouldDisplayActionButton && (
          <StatusButtons
            actionButtonArrangement={actionButtonArrangement}
            order={order}
            item={item}
            orderInternalStatus={orderInternalStatus}
            hasUnacceptedNotifications={hasUnacceptedNotifications}
            onStatusChanged={onStatusChanged}
            onPressedAction={onPressedAction}
          />
        )}
      </View>
      {!!item.special_instructions && (
        <View
          style={{
            marginTop: 10,
            borderTopWidth: 1,
            borderColor: '#FF0000',
            borderStyle: 'dotted',
            paddingTop: 8,
            flexDirection: 'row',
          }}
        >
          <Text color="#FF0000" numberOfLines={1} size={14}>
            {item.special_instructions}
          </Text>
        </View>
      )}
    </TouchableOpacity>
  );
};
